import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  cartransferSelector,
  setEntity,
  setId,
  setRepertory,
  setCartransfer,
  clearState,
  idSelector,
} from "../../../features/cartransferSlice";
import { useCreateCartransfer } from "../../../hooks/useCreateCartransfer";
import { useUpdateCartransfer } from "../../../hooks/useUpdateCartransfer";
import { useDeleteCartransfer } from "../../../hooks/useDeleteCartransfer";
import { useCartransferQuery } from "../../../hooks/useCartransferQuery";
import CartransferCreateSteps from "./CartransferCreateSteps";
import { Card, Spin, Skeleton } from "antd";
import useSocket from "../../../hooks/useSocket";

const CarTransferCreate = () => {
  const { cartransferId } = useParams();
  const dispatch = useDispatch();
  const finalData = useSelector(cartransferSelector);
  const id = useSelector(idSelector);
  const userId = useSelector((state) => state.auth.id);
  const [qr, setQr] = useState();
  const navigate = useNavigate();
  
  // Hooks de TanStack Query
  const createCartransfer = useCreateCartransfer();
  const updateCartransfer = useUpdateCartransfer();
  const deleteCartransfer = useDeleteCartransfer();
  const { data: cartransferData, isLoading: isLoadingCartransfer } = useCartransferQuery(cartransferId);

  // Usar useRef para evitar el bucle infinito
  const createCartransferRef = useRef(createCartransfer);
  createCartransferRef.current = createCartransfer;

  useEffect(() => {
    const fetchCartransfer = async () => {
      if (!userId) return;
      
      try {
        if (cartransferId && cartransferData) {
          if (!cartransferData?.id) {
            navigate("/admin/notfound");
            return;
          }
          setQr(cartransferData.id);

          // Resolver arrays: usar sellers[]/buyers[] si existen en BD, o envolver el singular
          const blank = { representsCompany: false };
          const resolvedSellers =
            cartransferData.sellers?.length > 0
              ? cartransferData.sellers.map(s => ({ representsCompany: false, ...s }))
              : cartransferData.seller && Object.keys(cartransferData.seller).length > 0
                ? [{ representsCompany: false, ...cartransferData.seller }]
                : [blank];
          const resolvedBuyers =
            cartransferData.buyers?.length > 0
              ? cartransferData.buyers.map(b => ({ representsCompany: false, ...b }))
              : cartransferData.buyer && Object.keys(cartransferData.buyer).length > 0
                ? [{ representsCompany: false, ...cartransferData.buyer }]
                : [blank];

          dispatch(setCartransfer({
            id: cartransferData.id,
            seller:     cartransferData.seller     || {},
            buyer:      cartransferData.buyer      || {},
            sellers:    resolvedSellers,
            buyers:     resolvedBuyers,
            background: cartransferData.background || {},
            permission: cartransferData.permission || {},
            contract:   cartransferData.contract   || {},
            repertory:      cartransferData.repertory     ?? null,
            repertoryYear:  cartransferData.repertoryYear ?? null,
          }));
        } else if (!cartransferId && !id) {
          // Solo crear si no tenemos un ID y no hemos creado ya uno
          const cartransfer = await createCartransferRef.current.mutateAsync({ employeeId: userId });
          if (!cartransfer?.data?.id) {
            navigate("/admin/notfound");
            return;
          }
          dispatch(setId(cartransfer.data.id));
          setQr(cartransfer.data.id);
        }
      } catch (error) {
        console.error('Error fetching cartransfer:', error);
      }
    };

    if (userId && !isLoadingCartransfer) {
      fetchCartransfer();
    }

    return () => {
      dispatch(clearState());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartransferId, userId, dispatch, navigate, cartransferData, isLoadingCartransfer]);


  // Usar el hook personalizado para Socket.IO
  useSocket(id, {
    phone_connected: () => {
      //commented because renders 4 times
      //toast("success", "Telefono conectado");
    },
    message: (data) => {
      dispatch(
        setEntity({
          entity: data.entity,
          data: data.data,
        })
      );
    }
  });

  const assignRepertory = async () => {
    try {
      const cartransfer = await updateCartransfer.mutateAsync({
        id,
        data: { status: "creado" }
      });
      const { repertory, repertoryYear } = cartransfer.data;

      dispatch(
        setRepertory({
          repertory,
          repertoryYear,
        })
      );
    } catch (error) {
      console.error('Error assigning repertory:', error);
    }
  };

  const discard = async () => {
    try {
      await deleteCartransfer.mutateAsync(id);
      navigate(`/admin/cartransfers/list`);
    } catch (error) {
      console.error('Error discarding cartransfer:', error);
    }
  };

  const saveChanges = async () => {
    try {
      await updateCartransfer.mutateAsync({
        id,
        data: {
          ...finalData,
          contract: {
            ...finalData.contract,
            repertory: `${finalData.repertory}-${finalData.repertoryYear}`,
          },
        },
      });
    } catch (error) {
      console.error('Error saving changes:', error);
    }
  };

  if (isLoadingCartransfer && cartransferId) {
    return (
      <Card style={{ margin: 15 }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </Card>
    );
  }

  if (!qr) {
    return <Card style={{ margin: 15 }}><Spin /></Card>;
  }

  return (
    <CartransferCreateSteps
      assignRepertory={assignRepertory}
      discard={discard}
      saveChanges={saveChanges}
      loading={updateCartransfer.isPending}
      cartransferId={cartransferId}
    />
  );
};

export default CarTransferCreate;
